import { ReactNode } from 'react';
import { css, cx } from '@linaria/atomic';
import { useMatchMedia, breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { LegacyButtonSize } from '../../buttons';
import { PrimaryButton } from '../../buttons/primary/primary-button';
import { SecondaryButton } from '../../buttons/secondary/secondary-button';
import { Text } from '../../text';
import { TextLink } from '../../text-link/text-link.component';
import { useStepperCardContext } from '../provider/stepper-card.provider';
import { useStepperContext } from '../provider/stepper.provider';

const helperTextStyle = css`
  margin: auto ${theme.spacing(1)};
  max-width: 300px;
`;

const alternateGridStyle = css`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-template-areas: 'tertiary helper secondary primary';
  gap: ${theme.spacing(2)};
  align-items: center;
`;

const alternateHelperTextStyle = css`
  grid-area: helper;
  justify-self: end;
`;

const tertiaryButtonStyle = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const alternateButtonStyle = css`
  display: flex;
  gap: ${theme.spacing(1)};
  width: auto;
  svg {
    margin: 0;
  }
`;

export type StepperButtonBarProps = {
  helperText?: string;
  children: ReactNode | ReactNode[];
  className?: string;
};

export const StepperButtonBar = ({ helperText, children, className, ...rest }: Partial<StepperButtonBarProps>) => {
  const smallMin = useMatchMedia({ minWidth: breakpoints.small.min });
  const { stepStatus } = useStepperContext();
  const { step } = useStepperCardContext();

  return (
    <>
      {(stepStatus[step] === 'active' || stepStatus[step] === 'currActive' || stepStatus[step] === 'errorActive') && (
        <div {...rest} className={cx('step-button-bar', className)}>
          {smallMin && !!helperText && (
            <Text as={'span'} className={helperTextStyle}>
              {helperText}
            </Text>
          )}
          {children}
        </div>
      )}
    </>
  );
};

type ButtonDefinition = {
  type: 'next' | 'previous' | 'skip' | 'none';
  position: 'primary' | 'secondary' | 'tertiary';
  onClick?: () => void;
  trackingId?: string;
  children: ReactNode;
  disabled?: boolean;
  skipTo?: number;
  size?: LegacyButtonSize;
  className?: string;
};

const generateButtonProps = ({
  button,
  step,
  nextFunction,
  skipFunction,
  previousFunction,
}: {
  button: ButtonDefinition;
  step: number;
  nextFunction?: (step: number) => void;
  skipFunction?: (skipTo: number, step: number) => void;
  previousFunction?: (step: number) => void;
}) => {
  switch (button.type) {
    case 'next': {
      const onClick = () => {
        if (button.onClick) {
          button.onClick();
        }
        nextFunction?.(step);
      };
      return {
        ...button,
        onClick,
      };
    }
    case 'previous': {
      const onClick = () => {
        if (button.onClick) {
          button.onClick();
        }
        previousFunction?.(step);
      };
      return {
        ...button,
        onClick,
      };
    }
    case 'skip': {
      const onClick = () => {
        if (button.onClick) {
          button.onClick();
        }
        if (button.skipTo) {
          skipFunction?.(button.skipTo, step);
        } else {
          nextFunction?.(step);
        }
      };
      return {
        ...button,
        onClick,
      };
    }
    default:
      return {
        ...button,
      };
  }
};

export const StepperButtonBarAlternate = ({
  helperText,
  buttons = [],
  className,
  children,
}: {
  helperText?: string;
  buttons?: ButtonDefinition[];
  className?: string;
  children?: ReactNode;
}) => {
  const smallMin = useMatchMedia({ minWidth: breakpoints.small.min });
  const { stepStatus } = useStepperContext();
  const { step } = useStepperCardContext();

  const resolvedClassName = className ? `${className} step-button-bar` : 'step-button-bar';

  if (!(stepStatus[step] === 'active' || stepStatus[step] === 'currActive' || stepStatus[step] === 'errorActive')) {
    return null;
  }

  return (
    <div className={resolvedClassName} style={{ flexDirection: 'column' }}>
      <div className={alternateGridStyle}>
        {smallMin && !!helperText && (
          <Text as={'span'} className={alternateHelperTextStyle} color='subdued'>
            {helperText}
          </Text>
        )}
        {children ?? buttons.map((button) => <StepperButtonAlternate key={button.position} {...button} />)}
      </div>
    </div>
  );
};

export const StepperButtonAlternate = (button: ButtonDefinition) => {
  const { nextFunction, skipFunction, previousFunction } = useStepperContext();
  const { step } = useStepperCardContext();
  const { position, disabled, onClick, trackingId, size, children, className } = generateButtonProps({
    button,
    step,
    nextFunction,
    skipFunction,
    previousFunction,
  });

  let component;
  if (position === 'tertiary') {
    component = (
      <TextLink
        size='large'
        disabled={disabled}
        weight='bold'
        className={cx('step-button', tertiaryButtonStyle, className)}
        style={{ gridArea: position }}
        onClick={onClick}
        trackingId={trackingId}
      >
        {children}
      </TextLink>
    );
  } else {
    const Button = position === 'primary' ? PrimaryButton : SecondaryButton;
    component = (
      <Button
        disabled={disabled}
        size={size ?? 'large'}
        onClick={onClick}
        className={cx('step-button', alternateButtonStyle, className)}
        style={{ gridArea: position }}
        trackingId={trackingId}
      >
        {children}
      </Button>
    );
  }
  return component;
};
